import React from "react";
import { useTranslation } from "react-i18next";

/**
 * v0.4.38 CP1 — Country regex format helpers for the VAT
 * Registrations table FORMAT column.
 *
 * Patterns from VIES 2026-06 reference + HMRC VAT format spec.
 * Returns one of:
 *   "VALID_FORMAT"   — passes the country-specific regex
 *   "INVALID"        — fails the regex
 *   "UNKNOWN"        — no regex for that country (don't claim valid/invalid)
 */
const PATTERNS = {
  AT: /^ATU\d{8}$/,
  BE: /^BE0\d{9}$/,
  BG: /^BG\d{9,10}$/,
  CY: /^CY\d{8}[A-Z]$/,
  CZ: /^CZ\d{8,10}$/,
  DE: /^DE\d{9}$/,
  DK: /^DK\d{8}$/,
  EE: /^EE\d{9}$/,
  ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
  FI: /^FI\d{8}$/,
  FR: /^FR[A-Z0-9]{2}\d{9}$/,
  GB: /^GB(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/,
  GR: /^(EL|GR)\d{9}$/,
  HR: /^HR\d{11}$/,
  HU: /^HU\d{8}$/,
  IE: /^IE\d{7}[A-Z]{1,2}$|^IE\d[A-Z0-9*+]\d{5}[A-Z]$/,
  IT: /^IT\d{11}$/,
  LT: /^LT(\d{9}|\d{12})$/,
  LU: /^LU\d{8}$/,
  LV: /^LV\d{11}$/,
  MT: /^MT\d{8}$/,
  NL: /^NL\d{9}B\d{2}$/,
  PL: /^PL\d{10}$/,
  PT: /^PT\d{9}$/,
  RO: /^RO\d{2,10}$/,
  SE: /^SE\d{12}$/,
  SI: /^SI\d{8}$/,
  SK: /^SK\d{10}$/,
};

export const vatFormatStatus = (country, number) => {
  const c = (country || "").trim().toUpperCase();
  const n = (number || "").trim().toUpperCase().replace(/\s+/g, "");
  if (!c) return "UNKNOWN";
  if (!n) return "INVALID";   // empty number with a country chosen
  const pat = PATTERNS[c];
  if (!pat) return "UNKNOWN";
  // Allow the VAT number to be entered with or without the
  // country-code prefix. Try both.
  if (pat.test(n)) return "VALID_FORMAT";
  if (pat.test(c + n)) return "VALID_FORMAT";
  return "INVALID";
};

const STATUS_TONE = {
  VALID_FORMAT: "bg-emerald-600/10 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  INVALID:      "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30",
  UNKNOWN:      "bg-slate-700/30 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700",
};

export const VatFormatChip = ({ country, number }) => {
  const { t } = useTranslation();
  const s = vatFormatStatus(country, number);
  const label = s === "VALID_FORMAT" ? t("ui.admin_clients.form.vat_format.valid", { defaultValue: "VALID" }) : s === "INVALID" ? t("ui.admin_clients.form.vat_format.invalid", { defaultValue: "INVALID" }) : "—";
  const tip = s === "VALID_FORMAT"
    ? t("ui.admin_clients.form.vat_format.tip_valid", { country, defaultValue: `Matches the ${country} VAT-number regex.` })
    : s === "INVALID"
      ? t("ui.admin_clients.form.vat_format.tip_invalid", { country: country || "?", defaultValue: `Does not match the ${country || "?"} VAT-number regex.` })
      : t("ui.admin_clients.form.vat_format.tip_unknown", { country: country || "?", defaultValue: `No regex available for ${country || "?"} — format not checked.` });
  return (
    <span
      title={tip}
      data-testid={`vat-format-${s}`}
      className={`inline-flex items-center px-2 py-1 tabular-nums text-[10px] tracking-normal border rounded ${STATUS_TONE[s]}`}
    >
      {label}
    </span>
  );
};

const SEVEN_DAYS = 1000 * 60 * 60 * 24 * 7;

export const effectiveDateWarning = (iso, tr = (_k, o) => o.defaultValue) => {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const now = Date.now();
  if (t > now) {
    return tr("ui.admin_clients.form.reg_date_future", { defaultValue: "This date is in the future — transactions before this date will not use this registration." });
  }
  if ((now - t) < SEVEN_DAYS) {
    return tr("ui.admin_clients.form.reg_date_recent", { defaultValue: "This registration date is very recent — confirm this is the actual date the registration started, not today's date. Transactions before this date will not use this registration." });
  }
  return null;
};
